import React, { useMemo } from "react";
import PropTypes from "prop-types";

import { Button, Tooltip, Typography } from "@mui/material";
import { Pencil, Users } from "lucide-react";
import Breadcrumbs from "../../../components/BreadCrumbs";
import Filters from "../../../components/Filter";
import ApplicantTagModal from "../../../components/Events/Event/Applicants/modals/ApplicantTagModal";
import DeleteTagConfirmModal from "../../../components/Events/Event/Applicants/modals/DeleteTagConfirmModal";
import {
  UIActionModal,
  UIBulkActions,
  UICountryFlag,
  UIEmptyState,
  UIPaginationBar,
  UIRating,
  UITable,
  UIUserInfo,
} from "../../../components/ui";

import "./LoyalApplicants.scss";

const LoyalApplicantsView = ({
  copy,
  shared,
  rows,
  pageSize,
  selectedIds,
  nationalityFilter,
  nationalityOptions,
  nationalitySearch,
  nationalityLoading,
  roleFilter,
  roleOptions,
  tagFilter,
  tagOptions,
  searchValue,
  selectedApplicants,
  myCompanyId,
  portalVariant,
  tagControls,
  isEditTagDisabled,
  isDeleteTagDisabled,
  canManageRowTag,
  removeModal,
  paginationTotal,
  paginationCurrentPage,
  paginationPages,
  paginationFrom,
  paginationTo,
  onSearchChange,
  onNationalityInputChange,
  onNationalityOpen,
  onNationalityChange,
  onRoleChange,
  onTagFilterChange,
  onPageChange,
  onPageSizeChange,
  onSelectionChange,
  onRowClick,
  onTagClick,
  onBulkEditTags,
  onBulkDeleteTags,
  onRemoveClick,
  onRemoveClose,
  onRemoveConfirm,
}) => {
  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: copy.columns.id,
        editable: false,
        filterable: false,
        hideable: false,
        hideSortIcons: true,
        sortable: false,
        width: 110,
        renderCell: (params) => (
          <span className="loyal-applicants__cell-text">
            {params.row.id ? `#${params.row.id}` : "-"}
          </span>
        ),
      },
      {
        field: "name",
        headerName: copy.columns.name,
        editable: false,
        filterable: false,
        hideable: false,
        hideSortIcons: true,
        sortable: false,
        flex: 2,
        minWidth: 240,
        cellClassName: "loyal-applicants__name-cell",
        headerClassName: "loyal-applicants__name-cell",
        renderCell: (params) => {
          const fullName = params.row.nameText || "-";
          return (
            <Tooltip title={fullName} placement="top" arrow>
              <span className="loyal-applicants__name-cell-inner">
                <UIUserInfo
                  name={fullName}
                  image={params.row.image}
                  className="loyal-applicants__user-info"
                />
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: "nationality",
        headerName: copy.columns.nationality,
        editable: false,
        filterable: false,
        hideable: false,
        hideSortIcons: true,
        sortable: false,
        flex: 1,
        minWidth: 130,
        renderCell: (params) => (
          <span className="loyal-applicants__cell-text">
            {params.row.nationality || "-"}
          </span>
        ),
      },
      {
        field: "role",
        headerName: copy.columns.role,
        editable: false,
        filterable: false,
        hideable: false,
        hideSortIcons: true,
        sortable: false,
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <span className="loyal-applicants__cell-text">
            {params.row.role || "-"}
          </span>
        ),
      },
      {
        field: "rating",
        headerName: copy.columns.rating,
        editable: false,
        filterable: false,
        hideable: false,
        hideSortIcons: true,
        sortable: false,
        width: 120,
        renderCell: (params) => (
          <UIRating value={params.row.rating ?? 0} readOnly size="small" />
        ),
      },
      {
        field: "tags",
        headerName: copy.columns.tags,
        editable: false,
        filterable: false,
        hideable: false,
        hideSortIcons: true,
        sortable: false,
        flex: 1.5,
        minWidth: 220,
        renderCell: (params) => {
          const { row } = params;

          const readonlyPill = row.userTag ? (
            <span className="event-applicants__tag-pill event-applicants__tag-pill--readonly">
              {row.tagName}
            </span>
          ) : (
            <Typography variant="caption" className="event-applicants__label">
              —
            </Typography>
          );

          if (!row.userTag) {
            return (
              <Button
                size="small"
                variant="outlined"
                dir="auto"
                className="event-applicants__tag-pill event-applicants__tag-pill--interactive"
                onClick={(event) => {
                  event.stopPropagation();
                  onTagClick(row);
                }}
              >
                {copy.columns.addTag}
              </Button>
            );
          }

          if (!canManageRowTag(row)) return readonlyPill;

          return (
            <Button
              size="small"
              variant="outlined"
              dir="auto"
              className="event-applicants__tag-pill event-applicants__tag-pill--interactive"
              endIcon={<Pencil size={14} strokeWidth={1.5} />}
              onClick={(event) => {
                event.stopPropagation();
                onTagClick(row);
              }}
            >
              {row.tagName}
            </Button>
          );
        },
      },
    ],
    [copy, onTagClick, canManageRowTag],
  );

  const breadcrumbList = [{ path: "", label: copy.breadcrumb }];

  return (
    <div className="loyal-applicants">
      <div className="page-title-margin">
        <Breadcrumbs list={breadcrumbList} BreadCrumbHasBackIcon={false} />
      </div>

      <Filters
        isCollapsable
        searchInput={{
          placeholder: copy.searchPlaceholder,
          name: "loyalApplicantsSearch",
          value: searchValue,
          onChange: onSearchChange,
        }}
        filters={[
          {
            type: "filter",
            name: "nationality",
            placeholder: copy.filters.nationality,
            value: nationalityFilter,
            options: nationalityOptions.map((option) => ({
              ...option,
              icon: (option.flag || option.shortCode) && (
                <UICountryFlag flag={option.flag} shortCode={option.shortCode} />
              ),
            })),
            isOptionsElt: true,
            loading: nationalityLoading,
            inputValue: nationalitySearch,
            filterOptions: (options) => options,
            onInputChange: onNationalityInputChange,
            onOpen: onNationalityOpen,
            onChange: onNationalityChange,
          },
          {
            type: "filter",
            name: "role",
            placeholder: shared.role,
            value: roleFilter,
            options: roleOptions,
            onChange: onRoleChange,
          },
          {
            type: "filter",
            name: "tag",
            placeholder: copy.filters.tag,
            value: tagFilter,
            options: tagOptions,
            onChange: onTagFilterChange,
          },
        ]}
        classNames={{
          container: "loyal-applicants__filters",
          base: "",
          search: "mb-3 mb-md-0",
          filter: "",
        }}
      />

      <UIBulkActions
        selectedCount={selectedIds.length}
        selectedLabel={copy.bulkActions.selectedLabel.replace("{count}", selectedIds.length)}
        actions={[
          {
            key: "remove",
            label: copy.bulkActions.remove,
            tone: "danger",
            onClick: onRemoveClick,
          },
        ]}
        menuItems={[
          {
            key: "editTags",
            label: copy.bulkActions.editTags,
            disabled: isEditTagDisabled,
            onClick: onBulkEditTags,
          },
          {
            key: "deleteTags",
            label: copy.bulkActions.deleteTags,
            tone: "danger",
            disabled: isDeleteTagDisabled,
            onClick: onBulkDeleteTags,
          },
        ]}
        className="loyal-applicants__bulk-actions"
      />

      <div className="loyal-applicants__content">
        {rows.length === 0 ? (
          <UIEmptyState
            classNames={{ root: "loyal-applicants__empty" }}
            title={copy.noDataDescription}
            description={copy.noDataSubDescription}
            icon={<Users size={40} strokeWidth={1.5} />}
          />
        ) : (
          <>
            <UITable
              rows={rows}
              columns={columns}
              checkboxSelection
              hideFooter
              contentSized
              pageSize={pageSize}
              selectionModel={selectedIds}
              onSelectionModelChange={onSelectionChange}
              onRowClick={({ row }) => onRowClick(row)}
            />
            <UIPaginationBar
              currentPage={paginationCurrentPage}
              pages={paginationPages}
              pageSize={pageSize}
              rangeTemplate={copy.showingRange}
              rangeFrom={paginationFrom}
              rangeTo={paginationTo}
              rangeTotal={paginationTotal}
              rowsPerPageLabel={shared.numberOfRows}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              className="loyal-applicants__pagination"
            />
          </>
        )}
      </div>

      <ApplicantTagModal
        open={Boolean(tagControls.selectedApplicant)}
        userTag={tagControls.selectedApplicant?.userTag}
        isBulk={tagControls.isBulkTag}
        selectedApplicants={selectedApplicants}
        tagOptions={tagOptions}
        portalVariant={portalVariant}
        myCompanyId={myCompanyId}
        onClose={tagControls.closeApplicantTag}
        onSave={tagControls.saveTag}
      />

      <DeleteTagConfirmModal
        open={tagControls.deleteTagTargetIds.length > 0}
        selectedApplicants={selectedApplicants}
        portalVariant={portalVariant}
        myCompanyId={myCompanyId}
        onClose={tagControls.closeDeleteTag}
        onConfirm={tagControls.confirmDeleteTag}
      />

      <UIActionModal
        open={removeModal.open}
        onClose={onRemoveClose}
        title={copy.removeModal.title}
        variant="reject"
        actions={
          <UIActionModal.Actions
            cancelLabel={copy.removeModal.cancel}
            confirmLabel={copy.removeModal.confirm}
            confirmVariant="reject"
            onCancel={onRemoveClose}
            onConfirm={onRemoveConfirm}
          />
        }
      >
        {selectedIds.length === 1
          ? copy.removeModal.messageSingle
          : copy.removeModal.messageMultiple.replace("{count}", selectedIds.length)}
      </UIActionModal>
    </div>
  );
};

LoyalApplicantsView.propTypes = {
  copy: PropTypes.object.isRequired,
  shared: PropTypes.object.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  pageSize: PropTypes.number.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))
    .isRequired,
  nationalityFilter: PropTypes.object,
  nationalityOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  nationalitySearch: PropTypes.string.isRequired,
  nationalityLoading: PropTypes.bool.isRequired,
  roleFilter: PropTypes.object,
  roleOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  tagFilter: PropTypes.object,
  tagOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  searchValue: PropTypes.string.isRequired,
  selectedApplicants: PropTypes.arrayOf(PropTypes.object).isRequired,
  myCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  portalVariant: PropTypes.string.isRequired,
  tagControls: PropTypes.object.isRequired,
  isEditTagDisabled: PropTypes.bool.isRequired,
  isDeleteTagDisabled: PropTypes.bool.isRequired,
  canManageRowTag: PropTypes.func.isRequired,
  removeModal: PropTypes.shape({
    open: PropTypes.bool.isRequired,
  }).isRequired,
  paginationTotal: PropTypes.number.isRequired,
  paginationCurrentPage: PropTypes.number.isRequired,
  paginationPages: PropTypes.number.isRequired,
  paginationFrom: PropTypes.number.isRequired,
  paginationTo: PropTypes.number.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onNationalityInputChange: PropTypes.func.isRequired,
  onNationalityOpen: PropTypes.func.isRequired,
  onNationalityChange: PropTypes.func.isRequired,
  onRoleChange: PropTypes.func.isRequired,
  onTagFilterChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  onTagClick: PropTypes.func.isRequired,
  onBulkEditTags: PropTypes.func.isRequired,
  onBulkDeleteTags: PropTypes.func.isRequired,
  onRemoveClick: PropTypes.func.isRequired,
  onRemoveClose: PropTypes.func.isRequired,
  onRemoveConfirm: PropTypes.func.isRequired,
};

LoyalApplicantsView.defaultProps = {
  nationalityFilter: null,
  roleFilter: null,
  tagFilter: null,
  myCompanyId: undefined,
};

export default LoyalApplicantsView;
